<script lang="ts">
	/**
	 * Top row of the header: support links, account access and language.
	 *
	 * Sits inside the header's right-hand column beside the logo rather than as a
	 * full-width band above it, matching dutch-passion.com — which is why it has no
	 * background of its own and only a rule beneath it.
	 *
	 * `/my-account` deliberately points at WordPress. Store API does not cover
	 * account management, and building an auth layer for ~81 customers on a
	 * checkout that is already guest-only is poor value — so this is one of the
	 * few links that leaves the static site.
	 */
	import { m } from '$lib/paraglide/messages';
	import { getLocale } from '$lib/paraglide/runtime';
	import { pagePath } from '$lib/pages';
	import SocialLinks from '../SocialLinks.svelte';
	import ThemeToggle from '../ThemeToggle.svelte';
	import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher.svelte';

	/**
	 * Paths are resolved through the page registry, not built by prefixing:
	 * WPML translates page slugs, so `/th/about-us` does not exist — the real
	 * URL is `/th/เกี่ยวกับเรา`. Untranslated pages are dropped rather than
	 * linked into a 404.
	 */
	const links = $derived(
		[
			{ key: 'contact', label: m.contact_us() },
			{ key: 'faq', label: m.faq() },
			{ key: 'about', label: m.about_us() }
		]
			.map((entry) => {
				const path = pagePath(entry.key, getLocale());
				return path ? { label: entry.label, href: path } : null;
			})
			.filter((link) => link !== null)
	);
</script>

<div class="flex items-center justify-between gap-6 border-b border-subtle py-2 text-sm">
	<SocialLinks size="sm" />

	<!--
		Hairline separators between the items, as on dutch-passion.com. `divide-x`
		rather than characters between labels, so nothing is announced to a screen
		reader that is not a link.
	-->
	<div class="flex items-center divide-x divide-subtle">
		{#each links as link (link.label)}
			<a
				href={link.href}
				class="px-4 text-muted transition-colors first:pl-0 hover:text-brand-500"
			>
				{link.label}
			</a>
		{/each}

		<!-- WordPress-served route: see the comment above. -->
		<a
			href="/my-account"
			data-sveltekit-reload
			class="flex items-center gap-1.5 px-4 font-medium text-body hover:underline"
		>
			<svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
				<path
					fill-rule="evenodd"
					clip-rule="evenodd"
					d="M10 9a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-7 8.5a7 7 0 0 1 14 0 .75.75 0 0 1-.75.75H3.75A.75.75 0 0 1 3 17.5Z"
				/>
			</svg>
			{m.sign_in()}
		</a>

		<!--
			Theme and language share one cell, with no `divide-x` rule between
			them: they are both "how this site is presented to me" controls, and a
			separator would file the theme toggle with the navigation links above
			instead. The group as a whole still takes the rule after Sign In.
		-->
		<div class="flex items-center gap-2 pl-4">
			<ThemeToggle />
			<LanguageSwitcher />
		</div>
	</div>
</div>
